import type { MouseEventHandler, ReactNode } from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import TextButton from 'LuxKit/Button/TextButton'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { sortBy } from 'lib/array/arrayUtils'
import Label from 'LuxKit/Label'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import BedbankMarginText from './BedbankMargin'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { useInView } from 'react-intersection-observer'
import { trackEvent } from 'analytics/analytics'
import { bedbankRateImpressionEventWithContext } from 'analytics/snowplow/events'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalContext from 'contexts/ModalContext'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PayLaterPill from 'components/Common/PayLaterPill'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import { getRelatedPayAtPropertyRate, hasMoreThanOnePropertyCollectRelation, shouldShowPayAtPropertyPill, shouldShowPayLaterPill } from '../utils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import BedbankPayAtPropertyModal from './Modals/BedbankPayAtPropertyModal'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import { useMetaOffersDiscount } from 'contexts/MetaOffersDiscountContext'
import RateImpressionContext from 'contexts/RateImpressionContext'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import { isOfferPageFullyPaidWithPoints } from 'lib/offer/isOfferPageFullyPaidWithPoints'
import OfferPagePaidEntirelyWithPointsLabel from 'components/OfferPage/Loyalty/OfferPagePaidEntirelyWithPointsLabel'
import useOfferPageBurnPoints, { useOfferPageBundledBurnPoints } from 'hooks/useOfferPageBurnPoints'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'

interface Props {
  promotion?: App.BedbankPromotion
  rate: App.BedbankRate
  payAtPropertyRates: Array<App.BedbankRate>
  bundleRate?: App.BedbankRate
  rooms: Array<OfferPageRoom>
  offer: App.BedbankOffer | App.BedbankOfferSummary
  flightOrigin?: App.Airport
  flightPrice?: number
  checkIn?: string
  checkOut?: string
  departureTooEarly?: boolean
  flightsEnabled?: boolean
  ratePosition: number
  rateListId?: string
  roomPosition?: number
  roomListId?: string
  luxPlusLPPEnabled: boolean
  isFlightsSelected: boolean
  showPoints: boolean
  onBookNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean,
    keepBrowsing?: boolean,
    isInsuranceSelected?: boolean,
    discount?: number,
  ) => void
  onAddToUserCartNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean,
    isInsuranceSelected?: boolean,
  ) => void
  isInsuranceSelected?: boolean
}

function BedbankPackageTilePricing(props: Props) {
  const {
    rate,
    payAtPropertyRates,
    bundleRate,
    promotion,
    rooms,
    offer,
    checkIn,
    checkOut,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    ratePosition,
    rateListId,
    roomPosition,
    roomListId,
    onBookNow,
    onAddToUserCartNow,
    luxPlusLPPEnabled,
    isFlightsSelected,
    showPoints,
    isInsuranceSelected,
  } = props

  const intl = useIntl()

  const showModal = useContext(ModalContext)
  const metaOffersDiscount = useMetaOffersDiscount()
  const { currentRegionCode } = useContext(GeoContext)
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: offer.flights?.airportCode,
  })
  const activeDeal = useMemo(
    () => (deals.length > 0 ? sortBy(deals, (d: App.FlightDeal) => d.perAdultPrice, 'asc')[0] : undefined),
    [deals],
  )
  const hasFlightPrice = !!flightPrice

  const {
    propertyFees = 0,
    taxesAndFees,
  } = rate.totals
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees

  const hotelPrice = rate.totals.inclusive
  const hotelMemberPrice = rate.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundleMemberPrice = bundleRate?.totals.memberInclusive || hotelMemberPrice
  const showFlightsDealTag = flightOrigin?.code && offer.flights?.airportCode && flightsEnabled && deals.length > 0

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return moment(checkIn).diff(moment.utc(), 'days') > 330
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const canAddToUserCart = hasDates

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue: rate.value,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue: bundleRate?.value,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const bundleDisplayTotals = getDisplayTotalPrice({
    hotelPrice: bundlePrice + bundlePropertyFees,
    hotelMemberPrice: bundleMemberPrice > 0 ? bundleMemberPrice + bundlePropertyFees : 0,
    hotelValue: bundleRate?.value,
    checkIn,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'total'
  const loyaltyProductType = getLoyaltyProductType(offer) ?? 'hotel_lpp_eps'
  const hotelBurnResult = useOfferPageBurnPoints(totalPrices.totals.price, loyaltyProductType)
  const displayFlightPrice = totalPrices.totals.flightBundlePrice - bundleDisplayTotals.totals.price
  const canShowFlightBundlePrice = flightsEnabled && hasFlightPrice && !departureTooEarly
  const flightBundleBurnResult = useOfferPageBundledBurnPoints({
    hotelPrice: bundleDisplayTotals.totals.price,
    hotelLoyaltyProductType: loyaltyProductType,
    flightPrice: displayFlightPrice,
    shouldUseSplitBurn: !!canShowFlightBundlePrice,
  })
  const isPromoRate = rate.id === promotion?.id
  const isPayAtPropertyFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croPayAtProperty)

  const margin = rate.margin
  const marginAud = rate.marginAud
  const supplier = rate.supplier
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)
  const displayMargin = Boolean(margin && marginAud && supplier && isSpoofed && showMarginInSpoofMode)

  // Use meta offers discount if available, otherwise fall back to rate discount
  const metaOffersDiscountPackage = metaOffersDiscount?.packages?.find(pkg => pkg.fkRoomTypeId === rate.room.roomId && pkg.rateId === rate.id)

  const discount = useBedbankDiscount(rate.discount, rate.memberDiscount)

  const bundleMargin = bundleRate?.margin
  const bundleMarginAud = bundleRate?.marginAud
  const bundleSupplier = bundleRate?.supplier
  const displayBundleMargin = Boolean(bundleMargin && bundleMarginAud && bundleSupplier && isSpoofed && showMarginInSpoofMode)

  const currency = useContext(CurrencyContext)
  const impressionContext = useContext(RateImpressionContext)

  const hotelImpressionKey = `${rate.id}:hotel`
  const flightBundleImpressionKey = `${bundleRate?.id ?? ''}:flight-bundle`

  const canFireImpression = !!(checkIn && checkOut && roomListId && roomPosition && rateListId)
  const canFireHotelImpression = canFireImpression && !isFlightsSelected
  const canFireFlightBundleImpression = !!(canFireImpression && isFlightsSelected && canShowFlightBundlePrice && bundleRate)

  const [hotelRef, hotelInView] = useInView({
    triggerOnce: true,
    rootMargin: '-100px 0px -100px 0px',
    threshold: 0.1,
    skip: !canFireHotelImpression,
  })

  const [flightBundleRef, flightBundleInView] = useInView({
    triggerOnce: true,
    rootMargin: '-100px 0px -100px 0px',
    threshold: 0.1,
    skip: !canFireFlightBundleImpression,
  })

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    hotelRef(node)
    flightBundleRef(node)
  }, [hotelRef, flightBundleRef])

  useEffect(() => {
    if (!(canFireHotelImpression || canFireFlightBundleImpression)) return

    const [impressionKey, inView] = canFireHotelImpression ?
        [hotelImpressionKey, hotelInView] :
        [flightBundleImpressionKey, flightBundleInView]
    if (impressionContext.hasFired(impressionKey) || !inView) return

    const partialContextData = canFireHotelImpression ?
        {
          rate,
          price: totalPrices.totals.price,
          value: totalPrices.totals.value,
          isFlightBundle: false,
        } :
        {
          rate: bundleRate!, // This is guaranteed by `canFireFlightBundleImpression`, but TS can't quite figure it out.
          price: totalPrices.totals.flightBundlePrice,
          value: totalPrices.totals.flightBundleValue,
          isFlightBundle: true,
        }

    trackEvent(bedbankRateImpressionEventWithContext({
      ratePosition,
      rateListId,
      roomPosition,
      roomListId,
      offer,
      checkIn,
      checkOut,
      occupancy: rooms,
      currency,
      ...partialContextData,
    }))
    impressionContext.markAsFired(impressionKey)
  }, [flightBundleInView, checkIn, checkOut, ratePosition, rateListId, roomPosition, roomListId, offer, bundleRate, totalPrices.totals.flightBundlePrice, totalPrices.totals.flightBundleValue, rooms, currency, canShowFlightBundlePrice, isFlightsSelected, impressionContext, flightBundleImpressionKey, canFireHotelImpression, canFireFlightBundleImpression, hotelImpressionKey, hotelInView, rate, totalPrices.totals.price, totalPrices.totals.value])

  const relatedPayAtPropertyRate = useMemo(() => getRelatedPayAtPropertyRate(payAtPropertyRates, rate), [payAtPropertyRates, rate])

  const getChosenRate = useCallback(async(hasFlight: boolean): Promise<App.BedbankRate | undefined> => {
    if (!hasFlight && isPayAtPropertyFeatureEnabled && relatedPayAtPropertyRate) {
      return await showModal<App.BedbankRate | undefined>(<BedbankPayAtPropertyModal payAtPropertyRate={relatedPayAtPropertyRate} lePaymentNowRate={rate} />)
    }
    return rate
  }, [relatedPayAtPropertyRate, rate, isPayAtPropertyFeatureEnabled, showModal])

  const onBookNowHandler = useMemo<MouseEventHandler<HTMLButtonElement> | undefined>(() => {
    if (!onBookNow) { return undefined }
    return async(event) => {
      const keepBrowsing: boolean = !!event.currentTarget.dataset.keepBrowsing
      const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight

      const chosenRate = await getChosenRate(hasFlight)
      if (!chosenRate) {
        return
      }

      onBookNow(chosenRate, ratePosition, hasFlight, keepBrowsing, isInsuranceSelected, metaOffersDiscountPackage?.discount)
    }
  }, [onBookNow, getChosenRate, ratePosition, isInsuranceSelected, metaOffersDiscountPackage?.discount])

  const onAddToUserCartHandler = useMemo<MouseEventHandler<HTMLButtonElement> | undefined>(() => {
    if (!onAddToUserCartNow) { return undefined }
    return async(event) => {
      const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight

      const chosenRate = await getChosenRate(hasFlight)
      if (!chosenRate) {
        return
      }

      onAddToUserCartNow(chosenRate, ratePosition, hasFlight, isInsuranceSelected)
    }
  }, [onAddToUserCartNow, getChosenRate, ratePosition, isInsuranceSelected])

  const showHotelAndFlightsToggle = flightsEnabled
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!showPoints) { return [] }
    return [
      generateLoyaltyPointsCalculatorBedbankOptions(rate, {
        price: totalPrices.totals.price,
        memberPrice: totalPrices.totals.memberPrice,
        propertyFees,
        propertyId: offer.property.id,
        offerId: offer.id,
        propertyCountryCode: offer.property.address.countryCode,
        currentRegionCode,
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !canShowFlightBundlePrice || hideHotelAndFlightsPriceBlock,
      }),
    ]
  }, [
    rate,
    totalPrices.totals.price,
    totalPrices.totals.memberPrice,
    propertyFees,
    offer,
    flightPrice,
    canShowFlightBundlePrice,
    hideHotelAndFlightsPriceBlock,
    showPoints,
    currentRegionCode,
  ])

  const flightsBookingActive = flightsEnabled && flightOrigin && isFlightsSelected
  const canPayAtProperty = !flightsBookingActive && shouldShowPayAtPropertyPill(rate)
  const canPayLater = shouldShowPayLaterPill(rate) && !hideHotelPriceBlock

  const priceCaptionText = useMemo(() => {
    const nightsText = intl.formatMessage(
      {
        id: 'LlNXnZeVARchKjjgMY8r',
        defaultMessage: '{nights, plural, one {# night} other {# nights}}',
        description: 'Nights count text for bedbank package tile price caption',
      },
      { nights: rate.nights },
    )
    const roomsText = rooms.length > 1 ? `, ${formatSaleUnitCount(intl, offer.saleUnit, rooms.length)}` : ''
    const fromText = hasDates ? intl.formatMessage({
      id: 'SVOG7nYao991Hu-W8pDD',
      defaultMessage: 'from',
      description: '"from" text for bedbank package tile price caption when dates are selected',
    }) : ''
    const fromSuffix = fromText ? ` ${fromText}` : ''
    return `${nightsText}${roomsText}${fromSuffix}`
  }, [intl, rate.nights, rooms.length, offer.saleUnit, hasDates])

  const flightsPriceCaptionText = useMemo(() => {
    const nightsText = intl.formatMessage(
      {
        id: 'X5Eg4VeHbGgvjrbFLBMj',
        defaultMessage: '{nights, plural, one {# night} other {# nights}}',
        description: 'Nights count text for bedbank flights package tile price caption',
      },
      { nights: rate.nights },
    )
    const roomsText = rooms.length > 1 ? `, ${formatSaleUnitCount(intl, offer.saleUnit, rooms.length)}` : ''
    const flightsText = intl.formatMessage({
      id: 'M3-w9fiec2oPMu9uEFut',
      defaultMessage: '+ flights from',
      description: 'Flights text for bedbank flights package tile price caption',
    })
    return `${nightsText}${roomsText} ${flightsText}`
  }, [intl, rate.nights, rooms.length, offer.saleUnit])

  const checkAvailabilityText = intl.formatMessage({
    id: 'Hp693KJ5TWYZTX6Da5lu',
    defaultMessage: 'Check availability',
    description: 'Check availability button text for bedbank package tile when dates not selected',
  })

  const bookNowText = intl.formatMessage({
    id: 'q7o047LRZlbUoW6hMWNQ',
    defaultMessage: 'Book now',
    description: 'Book now button text for bedbank package tile pricing',
  })

  const hotelOnlyBookNowText = useMemo(() => {
    if (rooms.length > 1) {
      return intl.formatMessage(
        {
          id: 'GJkW8x0u1wbk52WQxU_V',
          defaultMessage: 'Book {saleUnitLabel}',
          description: 'Book button text with room count for bedbank package tile pricing',
        },
        { saleUnitLabel: formatSaleUnitCount(intl, offer.saleUnit, rooms.length) },
      )
    }
    return intl.formatMessage({
      id: 'q7o047LRZlbUoW6hMWNQ',
      defaultMessage: 'Book now',
      description: 'Book now button text for bedbank package tile pricing',
    })
  }, [intl, rooms.length, offer.saleUnit])

  const bookNowButtonText = hasDates ? hotelOnlyBookNowText : checkAvailabilityText
  const flightsBookNowButtonText = hasDates ? bookNowText : checkAvailabilityText

  let priceContent: ReactNode = null

  if (metaOffersDiscountPackage) {
    if (flightsBookingActive) {
      priceContent = <VerticalSpacer gap={12} data-testid="flights-pricing">
        <Label kind="primary" variant="urgency">
          {intl.formatMessage({
            id: 'p_muneZohLq38f4hG_zE',
            defaultMessage: 'Limited time sale on select rooms',
            description: 'Limited time sale label for bedbank meta offers discount package',
          })}
        </Label>
        {!hasFlightPrice && flightOrigin.code === offer.flights?.airportCode && <BodyText
          variant="small"
          colour="light-foreground-alt"
        >
          {intl.formatMessage(
            {
              id: 'QQg7ek3v6jGL81PhIzy5',
              defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
              description: 'Message shown when no flights available from selected airport for bedbank flights',
            },
            { airportName: flightOrigin.name },
          )}
        </BodyText>}
        {!hasFlightPrice && flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'IkmpzVUnPQ64Em4q-C6i',
              defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
              description: 'Message encouraging user to add flights to bedbank hotel package',
            })}
          </BodyText>
        )}
        {!hasFlightPrice && departureTooFarInFuture && (
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'y7gyvmMQiNl_8NL7cvL7',
              defaultMessage: 'Flights can only be booked up to 11 months in advance.',
              description: 'Message shown when departure date is too far in future for bedbank flights',
            })}
          </BodyText>
        )}
        {flightOrigin.code !== offer.flights?.airportCode && departureTooEarly && <BodyText
          variant="small"
          colour="light-foreground-alt"
        >
          {intl.formatMessage({
            id: 'xEeYxJ0OBDaPHb4SWw4D',
            defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours',
            description: 'Message shown when check-in is too soon to book flights online for bedbank',
          })}
        </BodyText>}
        {canShowFlightBundlePrice && <VerticalSpacer gap={12}>
          {showFlightsDealTag && activeDeal && <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />}
          <div>
            <PriceRowPriceCaption>
              {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {flightsPriceCaptionText}
            </PriceRowPriceCaption>
            <PriceRowPrice
              size="M"
              price={metaOffersDiscountPackage.salePrice + (flightPrice || 0)}
              saleUnit={saleUnit}
            />
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            <PriceRowTaxesAndFees />
            <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={metaOffersDiscountPackage.price + (flightPrice || 0)}
              discountPercentage={metaOffersDiscountPackage.discountPercentage >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? metaOffersDiscountPackage.discountPercentage : undefined}
              discountType="estimate"
            />
            <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={bundlePropertyFees}
              taxesAndFees={bundleTaxesAndFees}
              hotelPrice={metaOffersDiscountPackage.salePrice}
              hotelMemberPrice={0}
              showMemberPrice={false}
              flightPrice={flightPrice}
              duration={rate.nights}
              hasRelatedPayAtPropertyRate={!!relatedPayAtPropertyRate}
              cta={<Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8} noDisplayWhenEmpty>
                {!hasDates && onBookNowHandler && <TextButton size="large" kind="primary" onClick={onBookNowHandler}>
                  {flightsBookNowButtonText}
                </TextButton>}
                {canAddToUserCart && onAddToUserCartHandler && <TextButton
                  size="large"
                  onClick={onAddToUserCartHandler}
                  data-has-flight
                  kind="primary"
                >
                  {intl.formatMessage({
                    id: 'PkeYTTSihyUEth-8Cv5j',
                    defaultMessage: 'Select package',
                    description: 'Select package button text for bedbank flights pricing',
                  })}
                </TextButton>}
              </Group>}
            />
            {displayBundleMargin && <BedbankMarginText
              margin={bundleMargin!}
              marginAud={bundleMarginAud!}
              supplier={bundleSupplier!}
              type="bedbank_exact_accom"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            pointsToApply={hotelBurnResult.pointsToBurn}
            burnPriceInPoints={hotelBurnResult.priceInPoints}
          />
        </VerticalSpacer>}
        <AtolProtectedTooltip />
      </VerticalSpacer>
    } else {
      priceContent = <VerticalSpacer gap={12} data-testid="pricing">
        <Label kind="primary" variant="urgency">
          {intl.formatMessage({
            id: 'p_muneZohLq38f4hG_zE',
            defaultMessage: 'Limited time sale on select rooms',
            description: 'Limited time sale label for bedbank meta offers discount package',
          })}
        </Label>
        <div>
          <PriceRowPriceCaption>
            {priceCaptionText}
          </PriceRowPriceCaption>
          <PriceRowPrice
            data-testid="bedbank-package-price"
            size="M"
            price={metaOffersDiscountPackage.salePrice}
            saleUnit={saleUnit}
          />
          <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={metaOffersDiscountPackage.price}
            discountPercentage={metaOffersDiscountPackage.discountPercentage}
            discountType="estimate"
          />
          {displayMargin && <BedbankMarginText
            margin={margin!}
            marginAud={marginAud!}
            supplier={supplier!}
            type="bedbank_exact"
          />}
        </div>
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType={!checkIn ? 'estimate' : 'exact'}
          pointsToApply={hotelBurnResult.pointsToBurn}
          burnPriceInPoints={hotelBurnResult.priceInPoints}
        />
      </VerticalSpacer>
    }
  } else {
    if (flightsBookingActive) {
      priceContent = <VerticalSpacer gap={12} data-testid="flights-pricing">
        {!hasFlightPrice && flightOrigin.code === offer.flights?.airportCode && <BodyText
          variant="small"
          colour="light-foreground-alt"
        >
          {intl.formatMessage(
            {
              id: 'QQg7ek3v6jGL81PhIzy5',
              defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
              description: 'Message shown when no flights available from selected airport for bedbank flights',
            },
            { airportName: flightOrigin.name },
          )}
        </BodyText>}
        {!hasFlightPrice && flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && (
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'IkmpzVUnPQ64Em4q-C6i',
              defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
              description: 'Message encouraging user to add flights to bedbank hotel package',
            })}
          </BodyText>
        )}
        {!hasFlightPrice && departureTooFarInFuture && (
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'y7gyvmMQiNl_8NL7cvL7',
              defaultMessage: 'Flights can only be booked up to 11 months in advance.',
              description: 'Message shown when departure date is too far in future for bedbank flights',
            })}
          </BodyText>
        )}
        {flightOrigin.code !== offer.flights?.airportCode && departureTooEarly && <BodyText
          variant="small"
          colour="light-foreground-alt"
        >
          {intl.formatMessage({
            id: 'xEeYxJ0OBDaPHb4SWw4D',
            defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours',
            description: 'Message shown when check-in is too soon to book flights online for bedbank',
          })}
        </BodyText>}
        {canShowFlightBundlePrice && <VerticalSpacer gap={12}>
          {showFlightsDealTag && activeDeal && <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />}
          <div>
            {isOfferPageFullyPaidWithPoints(
              flightBundleBurnResult.pointsToBurn,
              flightBundleBurnResult.priceInPoints,
              flightBundleBurnResult.hideZeroCash,
            ) && <OfferPagePaidEntirelyWithPointsLabel />}
            <PriceRowPriceCaption>
              {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {flightsPriceCaptionText}
            </PriceRowPriceCaption>
            {flightBundleBurnResult.enabled && flightBundleBurnResult.priceInPoints !== undefined && <OfferPageBurnPriceDisplay
              totalPrice={totalPrices.totals.flightBundlePrice}
              pointsToApply={flightBundleBurnResult.pointsToBurn}
              priceInPoints={flightBundleBurnResult.priceInPoints}
              pointsHideZeroCash={flightBundleBurnResult.hideZeroCash}
              memberPrice={totalPrices.totals.flightBundleMemberPrice}
              size="M"
              saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total'}
            />}
            {!flightBundleBurnResult.enabled && <LuxPlusPriceGuard
              showMemberPrice={(totalPrices.totals.flightBundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
              fallbackPrice={<PriceRowPrice
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                saleUnit={saleUnit}
              />}
            >
              <LuxPlusPriceStack
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                memberPrice={totalPrices.totals.flightBundleMemberPrice}
                saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total*'}
              />
            </LuxPlusPriceGuard>}
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            <PriceRowTaxesAndFees />
            {discount > 0 && !isPromoRate && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={totalPrices.totals.flightBundleValue}
              discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
              discountType="estimate"
            />}
            {!isPromoRate && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={bundlePropertyFees}
              taxesAndFees={bundleTaxesAndFees}
              hotelPrice={bundlePrice}
              hotelMemberPrice={bundleMemberPrice}
              showMemberPrice={luxPlusLPPEnabled && bundleMemberPrice > 0}
              flightPrice={flightPrice}
              duration={rate.nights}
              hasRelatedPayAtPropertyRate={!!relatedPayAtPropertyRate}
              cta={<Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8} noDisplayWhenEmpty>
                {!hasDates && onBookNowHandler && <TextButton size="large" kind="primary" onClick={onBookNowHandler}>
                  {flightsBookNowButtonText}
                </TextButton>}
                {canAddToUserCart && onAddToUserCartHandler && <TextButton
                  size="large"
                  onClick={onAddToUserCartHandler}
                  data-has-flight
                  kind="primary"
                >
                  {intl.formatMessage({
                    id: 'PkeYTTSihyUEth-8Cv5j',
                    defaultMessage: 'Select package',
                    description: 'Select package button text for bedbank flights pricing',
                  })}
                </TextButton>}
              </Group>}
            />}
            {displayBundleMargin && <BedbankMarginText
              margin={bundleMargin!}
              marginAud={bundleMarginAud!}
              supplier={bundleSupplier!}
              type="bedbank_exact_accom"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            pointsToApply={flightBundleBurnResult.pointsToBurn}
            burnPriceInPoints={flightBundleBurnResult.priceInPoints}
          />
        </VerticalSpacer>}
        <AtolProtectedTooltip />
      </VerticalSpacer>
    } else {
      priceContent = <VerticalSpacer gap={12} data-testid="pricing">
        <div>
          {isOfferPageFullyPaidWithPoints(
            hotelBurnResult.pointsToBurn,
            hotelBurnResult.priceInPoints,
            hotelBurnResult.hideZeroCash,
          ) && <OfferPagePaidEntirelyWithPointsLabel />}
          <PriceRowPriceCaption>
            {priceCaptionText}
          </PriceRowPriceCaption>
          {hotelBurnResult.enabled && hotelBurnResult.priceInPoints !== undefined && <OfferPageBurnPriceDisplay
            totalPrice={totalPrices.totals.price}
            pointsToApply={hotelBurnResult.pointsToBurn}
            priceInPoints={hotelBurnResult.priceInPoints}
            pointsHideZeroCash={hotelBurnResult.hideZeroCash}
            memberPrice={totalPrices.totals.memberPrice}
            size="M"
            saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total'}
          />}
          {!hotelBurnResult.enabled && <LuxPlusPriceGuard
            showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
            fallbackPrice={<PriceRowPrice
              data-testid="bedbank-package-price"
              size="M"
              price={totalPrices.totals.price}
              saleUnit={saleUnit}
            />}
          >
            <LuxPlusPriceStack
              size="M"
              price={totalPrices.totals.price}
              memberPrice={totalPrices.totals.memberPrice}
              saleUnit={saleUnit}
            />
          </LuxPlusPriceGuard>}
          {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
          {!isPromoRate && <PriceRowTaxesAndFees />}

          <PriceRowAgentHubCommission
            productType={offer.productType}
            offerLocations={[]}
            offerId={offer.id}
            packageId={rate.id}
            offerTotal={totalPrices.original.price}
            kind="plain"
          />

          {!!discount && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={totalPrices.totals.value}
            discountPercentage={discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discount : undefined}
            discountType="estimate"
          />}
          {!isPromoRate && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offerPage
            offer={offer}
            rooms={rooms}
            propertyFees={propertyFees}
            taxesAndFees={taxesAndFees}
            hotelPrice={hotelPrice}
            hotelMemberPrice={hotelMemberPrice}
            showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
            duration={rate.nights}
            hasMobilePromotion={rate.mobilePromotion}
            hasRelatedPayAtPropertyRate={!!relatedPayAtPropertyRate}
            rateId={rate.id}
            cta={<Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8} noDisplayWhenEmpty>
              {onBookNowHandler && <TextButton size="large" kind="primary" onClick={onBookNowHandler}>
                {bookNowButtonText}
              </TextButton>}
              {canAddToUserCart && onAddToUserCartHandler && <TextButton
                size="large"
                onClick={onAddToUserCartHandler}
                startIcon={<LineShoppingCartIcon />}
                kind="secondary"
              >
                {intl.formatMessage({
                  id: 'g0EvhYoaCd_EaQLo44w7',
                  defaultMessage: 'Add to cart',
                  description: 'Add to cart button text for bedbank package tile on the offer detail page',
                })}
              </TextButton>}
            </Group>}
          />}
          {displayMargin && <BedbankMarginText
            margin={margin!}
            marginAud={marginAud!}
            supplier={supplier!}
            type="bedbank_exact"
          />}
        </div>
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType={!checkIn ? 'estimate' : 'exact'}
          pointsToApply={hotelBurnResult.pointsToBurn}
          burnPriceInPoints={hotelBurnResult.priceInPoints}
        />
      </VerticalSpacer>
    }
  }

  return <>
    {!!(canPayLater || canPayAtProperty) && <Group direction="horizontal" gap={4}>
      {canPayLater && <PayLaterPill />}
      {canPayAtProperty && <PayAtPropertyPill hasMultipleOptions={hasMoreThanOnePropertyCollectRelation(rate)} />}
    </Group>}
    <div ref={setRefs}>
      {priceContent}
    </div>
  </>
}

type MappedProps = Pick<Props,
  | 'luxPlusLPPEnabled'
  | 'isFlightsSelected'
  | 'checkIn'
  | 'checkOut'
  | 'flightsEnabled'
  | 'flightOrigin'
  | 'offer'
  | 'rooms'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  isFlightsSelected: state.flights,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  flightOrigin: state.airport,
  offer: state.bedbankOffer!,
  rooms: state.rooms,
}), BedbankPackageTilePricing)
